import { useContext, useEffect, useState } from 'react'
import { DEMO_CALCULATOR_STEP } from 'config/constants'

import { isDemoPage } from '../../../../../utils/hooks'
import {
  DEMO_BANK_COMMISSION_INVOICE_READY_EVENT,
  DEMO_CALCULATOR_STEPS,
} from '../../playback-constants'
import { DemoPlayerContext } from '../../player-clock'
import { getDemoBankCommissionsByInvoiceNumber } from '../../scenario'

const getPaymentFormValue = (value: string | number | undefined) => (
  value == null ? '' : String(value)
)

const getPaymentFormValues = (
  bankCommission: BankCommissions | TestDriveBankCommission,
) => ({
  bank_transfer_fee: getPaymentFormValue(bankCommission.bank_transfer_fee),
  commission_percentage: getPaymentFormValue(bankCommission.commission_percentage),
  currency_control_fee: getPaymentFormValue(bankCommission.currency_control_fee),
  max_bank_transfer_fee: getPaymentFormValue(bankCommission.max_bank_transfer_fee),
  max_currency_control_fee: getPaymentFormValue(bankCommission.max_currency_control_fee),
  min_bank_transfer_fee: getPaymentFormValue(bankCommission.min_bank_transfer_fee),
  min_currency_control_fee: getPaymentFormValue(bankCommission.min_currency_control_fee),
  payment_amount: getPaymentFormValue(bankCommission.payment_amount),
})

export const getDemoInitialPaymentValues = (invoiceNumber: string) => {
  if (!isDemoPage()) {
    return undefined
  }

  const invoice = getDemoBankCommissionsByInvoiceNumber(invoiceNumber)
  const payments = invoice?.banking_commissions.map(getPaymentFormValues)

  return payments?.length ? payments : undefined
}

export const useDemoBankInvoiceCompletion = () => {
  const isDemoMode = isDemoPage()
  const player = useContext(DemoPlayerContext)
  const paymentStepIndex = DEMO_CALCULATOR_STEPS.indexOf(DEMO_CALCULATOR_STEP.BANKING_COMMISSIONS)
  const [ isComplete, setIsComplete ] = useState((player?.startStep ?? 0) > paymentStepIndex)

  return {
    isComplete: isDemoMode ? isComplete : undefined,
    onSaved: isDemoMode ? () => setIsComplete(true) : undefined,
  }
}

export const useDemoBankInvoice = (invoiceId: string, productionDisabled: boolean) => {
  const isDemoMode = isDemoPage()
  const [ isPlaybackReady, setIsPlaybackReady ] = useState(false)

  useEffect(() => {
    if (!isDemoMode) {
      return
    }

    const handleReady = (event: Event) => {
      const currentInvoiceId = (event as CustomEvent<{ invoiceId: string }>).detail?.invoiceId

      if (currentInvoiceId === invoiceId) {
        setIsPlaybackReady(true)
      }
    }

    window.addEventListener(DEMO_BANK_COMMISSION_INVOICE_READY_EVENT, handleReady)

    return () => window.removeEventListener(DEMO_BANK_COMMISSION_INVOICE_READY_EVENT, handleReady)
  }, [ invoiceId, isDemoMode ])

  return {
    invoiceProps: {
      'data-demo-bank-commission-invoice': isDemoMode ? invoiceId : undefined,
      'data-demo-playback-action': isDemoMode ? 'save-bank-commission-invoice' : undefined,
      'data-demo-allowed': isDemoMode ? 'save-bank-commission-invoice' : undefined,
    },
    isDemoMode,
    saveButtonDisabled: isDemoMode ? !isPlaybackReady : productionDisabled,
    shouldShowPaymentTitle: !isDemoMode,
  }
}

export const useDemoBankCommissionSave = () => {
  const isDemoMode = isDemoPage()
  const [ isComplete, setIsComplete ] = useState(false)

  return {
    complete: () => setIsComplete(true),
    isDemoMode,
    saveButtonProps: {
      'data-demo-playback-action': isDemoMode ? 'save-bank-commissions' : undefined,
      'data-demo-playback-complete': isDemoMode && isComplete ? 'true' : undefined,
    },
  }
}

export const useDemoBankInvoiceAccordion = (invoiceId: number) => {
  const isDemoMode = isDemoPage()

  return {
    accordionProps: {
      expanded: isDemoMode ? true : undefined,
    },
    invoiceProps: {
      'data-demo-bank-commission-invoice': isDemoMode ? invoiceId : undefined,
    },
  }
}
